import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Subject, takeUntil } from 'rxjs';
import { Store } from '@ngrx/store';

import { ComposePanelComponent } from '../compose-panel/compose-panel.component';
import { ComposeService } from '../../../../services/compose.service';
import type { IComposePanel } from '../../../../models/compose.models';
import { AppState } from 'src/app/core/app.state';
import * as smartInboxActions from '../../../../store/smart-inbox.actions';

/**
 * Full-viewport overlay opened as a single MatDialog. Renders one
 * ComposePanelComponent per visible panel; stacking is handled via z-index.
 */
@Component({
  selector: 'app-compose-overlay-host',
  standalone: true,
  templateUrl: './compose-overlay-host.component.html',
  styleUrls: ['./compose-overlay-host.component.scss'],
  imports: [CommonModule, FormsModule, ComposePanelComponent],
})
export class ComposeOverlayHostComponent implements OnInit, OnDestroy {

  // ── Public properties ──────────────────────────────────────────────────────

  panels: IComposePanel[] = [];

  // ── Private properties ─────────────────────────────────────────────────────

  private destroy$ = new Subject<void>();

  // ── Constructor ────────────────────────────────────────────────────────────

  constructor(
    public dialogRef: MatDialogRef<ComposeOverlayHostComponent>,
    private composeService: ComposeService,
    private store: Store<AppState>,
  ) {}

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.composeService.panels.pipe(takeUntil(this.destroy$)).subscribe((panels) => {
      this.panels = panels;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Computed (template getters) ────────────────────────────────────────────

  get visiblePanels(): IComposePanel[] {
    return this.panels.filter((p) => !p.minimized);
  }

  // ── Event handlers ────────────────────────────────────────────────────────

  onCancel(id: string): void {
    this.composeService.closeCompose(id);
  }

  onSend(id: string): void {
    const panel = this.panels.find((p) => p.id === id);
    const replyToConversationId = panel?.reply_to_conversation?.id;
    this.store.dispatch(smartInboxActions.SendCompose({ panelId: id, replyToConversationId }));
  }

  onBringToFront(id: string): void {
    this.composeService.bringToFront(id);
  }

  onMinimize(id: string): void {
    this.composeService.setMinimized(id, true);
  }

  onPositionChanged(id: string, pos: { x: number; y: number }): void {
    this.composeService.updatePanelPosition(id, pos.x, pos.y);
  }

  onDeleteDraft(panelId: string): void {
    this.composeService.deleteDraft(panelId);
  }

  // ── Template helpers ──────────────────────────────────────────────────────

  trackByPanelId(_index: number, panel: IComposePanel): string {
    return panel.id;
  }
}
